import { ref, onMounted, onUnmounted } from "vue";

/**
 * SSE composable for browser source alerts
 * Connects to the backend SSE endpoint to receive real-time events
 */
export function useServerEvents(options = {}) {
  const {
    baseUrl = import.meta.env.VITE_API_URL,
    token = null,
    autoConnect = false,
    refreshToken = null,
  } = options;

  const eventSource = ref(null);
  const isConnected = ref(false);
  const error = ref(null);
  const reconnectAttempts = ref(0);
  const maxReconnectAttempts = 10;
  const listeners = new Map();

  let reconnectTimeout = null;
  let currentToken = null;

  function connect(authToken = token) {
    if (eventSource.value) {
      console.warn("[SSE] Already connected");
      return;
    }

    if (!authToken) {
      error.value = "No auth token provided";
      console.error("[SSE] No auth token provided");
      return;
    }

    currentToken = authToken;
    const url = `${baseUrl}/events?token=${encodeURIComponent(authToken)}`;

    try {
      eventSource.value = new EventSource(url);

      eventSource.value.onopen = () => {
        isConnected.value = true;
        error.value = null;
        reconnectAttempts.value = 0;
        if (reconnectTimeout) {
          clearTimeout(reconnectTimeout);
          reconnectTimeout = null;
        }
      };

      eventSource.value.onerror = (e) => {
        console.error("[SSE] Connection error:", e);
        error.value = "Connection error";
        isConnected.value = false;

        // Don't rely on the native auto-reconnect: it keeps retrying the same
        // URL with the (possibly expired) token forever. Tear down and drive our
        // own backoff + token refresh instead.
        if (eventSource.value) {
          eventSource.value.close();
          eventSource.value = null;
        }
        scheduleReconnect();
      };

      eventSource.value.addEventListener("connected", (e) => {
        const data = JSON.parse(e.data);
      });

      eventSource.value.addEventListener("ping", () => {});

      for (const [eventType, callbacks] of listeners) {
        for (const callback of callbacks) {
          attachListener(eventType, callback);
        }
      }
    } catch (e) {
      console.error("[SSE] Failed to connect:", e);
      error.value = e.message;
      scheduleReconnect();
    }
  }

  function scheduleReconnect() {
    // A reconnect is already pending — onerror can fire repeatedly, don't stack.
    if (reconnectTimeout) return;

    // This overlay runs 24/7: never give up permanently. After the initial
    // exponential backoff we keep retrying at a steady capped interval, so the
    // alerts come back on their own once the network/token recovers — no OBS
    // restart required. `maxReconnectAttempts` is just where the backoff plateaus.
    const cappedAttempts = Math.min(
      reconnectAttempts.value,
      maxReconnectAttempts,
    );
    const delay = Math.min(1000 * Math.pow(2, cappedAttempts), 30000);
    if (reconnectAttempts.value >= maxReconnectAttempts) {
      console.warn("[SSE] Still down — retrying every", delay, "ms");
      error.value = "Connection lost — retrying…";
    }

    reconnectTimeout = setTimeout(async () => {
      reconnectTimeout = null; // allow a future error to schedule again
      reconnectAttempts.value++;
      if (refreshToken) {
        try {
          currentToken = await refreshToken();
        } catch (e) {
          console.error("[SSE] Token refresh failed:", e);
          error.value = "Token refresh failed";
          scheduleReconnect();
          return;
        }
      }
      connect(currentToken);
    }, delay);
  }

  /**
   * Disconnect from SSE
   */
  function disconnect() {
    if (reconnectTimeout) {
      clearTimeout(reconnectTimeout);
      reconnectTimeout = null;
    }

    if (eventSource.value) {
      eventSource.value.close();
      eventSource.value = null;
      isConnected.value = false;
    }
  }

  /**
   * Attach a listener to the EventSource
   */
  function attachListener(eventType, callback) {
    if (!eventSource.value) return;

    eventSource.value.addEventListener(eventType, (e) => {
      try {
        const data = JSON.parse(e.data);
        callback(data, e);
      } catch {
        callback(e.data, e);
      }
    });
  }

  /**
   * Add an event listener
   */
  function addEventListener(eventType, callback) {
    if (!listeners.has(eventType)) {
      listeners.set(eventType, new Set());
    }
    listeners.get(eventType).add(callback);

    // Attach immediately if already connected
    if (eventSource.value) {
      attachListener(eventType, callback);
    }

    // Return cleanup function
    return () => {
      listeners.get(eventType)?.delete(callback);
    };
  }

  /**
   * Remove all listeners for an event type
   */
  function removeAllListeners(eventType) {
    listeners.delete(eventType);
  }

  onMounted(() => {
    if (autoConnect && token) {
      connect();
    }
  });

  onUnmounted(() => {
    disconnect();
  });

  return {
    isConnected,
    error,
    reconnectAttempts,
    connect,
    disconnect,
    addEventListener,
    removeAllListeners,
  };
}

export default useServerEvents;

